/**
 * Minimal markdown render — AI rapor + demo rapor için
 *
 * Desteklenen syntax:
 *  - # Başlık, ## Alt başlık, ### Daha alt
 *  - - Liste maddeleri
 *  - **bold**
 *  - *italic*
 *  - [text](url) — relative veya https:// için
 *  - --- yatay çizgi
 *  - > Alıntı (single-line)
 *  - inline `code`
 *
 * Security:
 *  - Sadece relative URL ya da https:// linklere izin
 *  - HTML enjekte edilmez (React text node'lar)
 */
import type React from 'react';

export function ReportMarkdown({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let listBuffer: string[] = [];

  function flushList(): void {
    if (listBuffer.length > 0) {
      elements.push(
        <ul key={`l-${elements.length}`} className="my-3 list-disc space-y-1 pl-5">
          {listBuffer.map((item, i) => (
            <li key={i} className="text-slate-700">
              {renderInline(item)}
            </li>
          ))}
        </ul>,
      );
      listBuffer = [];
    }
  }

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(
        <h2
          key={`h2-${elements.length}`}
          className="mt-5 text-xl font-bold text-slate-900 first:mt-0"
        >
          {trimmed.slice(2)}
        </h2>,
      );
    } else if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h3
          key={`h3-${elements.length}`}
          className="mt-4 text-lg font-semibold text-slate-900"
        >
          {trimmed.slice(3)}
        </h3>,
      );
    } else if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h4
          key={`h4-${elements.length}`}
          className="mt-3 text-base font-semibold text-slate-900"
        >
          {trimmed.slice(4)}
        </h4>,
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listBuffer.push(trimmed.slice(2));
    } else if (trimmed === '---') {
      flushList();
      elements.push(
        <hr key={`hr-${elements.length}`} className="my-4 border-slate-200" />,
      );
    } else if (trimmed.startsWith('> ')) {
      flushList();
      elements.push(
        <blockquote
          key={`q-${elements.length}`}
          className="my-3 border-l-4 border-slate-300 bg-slate-50 px-4 py-2 text-slate-600 italic"
        >
          {renderInline(trimmed.slice(2))}
        </blockquote>,
      );
    } else if (trimmed === '') {
      flushList();
    } else {
      flushList();
      elements.push(
        <p key={`p-${elements.length}`} className="my-2 text-slate-700">
          {renderInline(trimmed)}
        </p>,
      );
    }
  }
  flushList();

  return <div className="prose prose-slate max-w-none">{elements}</div>;
}

/** Inline markdown: **bold**, *italic*, [text](url), `code` */
function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  let safetyCounter = 0;

  while (remaining.length > 0 && safetyCounter < 500) {
    safetyCounter++;
    // Link [text](url)
    const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)/.exec(remaining);
    if (linkMatch && linkMatch[2]) {
      const whole = linkMatch[0];
      const label = linkMatch[1] ?? '';
      const href = linkMatch[2];
      const safeHref =
        href.startsWith('/') || href.startsWith('https://') ? href : '#';
      const isExternal = safeHref.startsWith('https://');
      parts.push(
        <a
          key={key++}
          href={safeHref}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noreferrer' : undefined}
          className="font-medium text-blue-900 underline"
        >
          {label}
        </a>,
      );
      remaining = remaining.slice(whole.length);
      continue;
    }
    // Bold **text**
    const boldMatch = /^\*\*([^*]+)\*\*/.exec(remaining);
    if (boldMatch) {
      const [whole, content] = boldMatch;
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900">
          {content}
        </strong>,
      );
      remaining = remaining.slice(whole.length);
      continue;
    }
    // Italic *text* (yalnız tek *)
    const italicMatch = /^\*([^*]+)\*/.exec(remaining);
    if (italicMatch) {
      const [whole, content] = italicMatch;
      parts.push(
        <em key={key++} className="italic">
          {content}
        </em>,
      );
      remaining = remaining.slice(whole.length);
      continue;
    }
    // Inline code `code`
    const codeMatch = /^`([^`]+)`/.exec(remaining);
    if (codeMatch) {
      const [whole, content] = codeMatch;
      parts.push(
        <code
          key={key++}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-sm text-slate-800"
        >
          {content}
        </code>,
      );
      remaining = remaining.slice(whole.length);
      continue;
    }
    // Bir sonraki marker'a kadar plain text
    const nextMarker = remaining.search(/[\[*`]/);
    if (nextMarker === -1) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
    if (nextMarker === 0) {
      // Marker var ama tanınmıyor — bir karakter ilerle
      parts.push(<span key={key++}>{remaining[0]}</span>);
      remaining = remaining.slice(1);
      continue;
    }
    parts.push(<span key={key++}>{remaining.slice(0, nextMarker)}</span>);
    remaining = remaining.slice(nextMarker);
  }
  return parts;
}
